
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { QuoteBlockConfiguration } from "@/types/block-configurations";
import {
  TypographyControls,
  AlignmentControls,
  PaddingControls,
  BackgroundControls,
  BorderControls,
  ResponsiveControls,
} from "../shared";

interface QuoteBlockPropertiesProps {
  config: QuoteBlockConfiguration;
  onChange: (config: QuoteBlockConfiguration) => void;
}

export function QuoteBlockProperties({ config, onChange }: QuoteBlockPropertiesProps) {
  const updateField = <K extends keyof QuoteBlockConfiguration>(
    field: K,
    value: QuoteBlockConfiguration[K]
  ) => {
    onChange({ ...config, [field]: value });
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs font-medium mb-1 block">
          Quote Text <span className="text-destructive">*</span>
        </Label>
        <textarea
          value={config.text}
          onChange={(e) => updateField('text', e.target.value)}
          placeholder="Enter the quote text..."
          rows={3}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-medium mb-1 block">Author</Label>
          <Input
            type="text"
            value={config.author ?? ''}
            onChange={(e) => updateField('author', e.target.value || undefined)}
            placeholder="Jane Doe"
          />
        </div>
        <div>
          <Label className="text-xs font-medium mb-1 block">Source</Label>
          <Input
            type="text"
            value={config.source ?? ''}
            onChange={(e) => updateField('source', e.target.value || undefined)}
            placeholder="Book / Company"
          />
        </div>
      </div>

      <div>
        <Label className="text-xs font-medium mb-1 block">Source URL</Label>
        <Input
          type="url"
          value={config.sourceUrl ?? ''}
          onChange={(e) => updateField('sourceUrl', e.target.value || undefined)}
          placeholder="https://example.com"
        />
      </div>

      <div>
        <Label className="text-xs font-medium mb-1 block">Quote Marks</Label>
        <Select
          value={config.quoteMarks}
          onChange={(e) => updateField('quoteMarks', e.target.value as QuoteBlockConfiguration['quoteMarks'])}
        >
          <option value="none">None</option>
          <option value="single">Single (' ')</option>
          <option value="double">Double (" ")</option>
          <option value="guillemet">Guillemet (« »)</option>
        </Select>
      </div>

      <Accordion type="multiple" defaultValue={["typography"]} className="w-full">

        <AccordionItem value="typography">
          <AccordionTrigger>Typography</AccordionTrigger>
          <AccordionContent>
            <TypographyControls
              value={config.typography}
              onChange={(typography) => updateField('typography', typography)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="alignment">
          <AccordionTrigger>Alignment</AccordionTrigger>
          <AccordionContent>
            <AlignmentControls
              value={config.alignment}
              onChange={(alignment) => updateField('alignment', alignment)}
              showVertical={false}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="background">
          <AccordionTrigger>Background</AccordionTrigger>
          <AccordionContent>
            <BackgroundControls
              value={config.background}
              onChange={(background) => updateField('background', background)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="spacing">
          <AccordionTrigger>Spacing</AccordionTrigger>
          <AccordionContent>
            <PaddingControls
              value={config.padding}
              onChange={(padding) => updateField('padding', padding)}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="border">
          <AccordionTrigger>Accent Bar</AccordionTrigger>
          <AccordionContent>
            {/* Renders as a left accent bar on the blockquote; radius isn't supported */}
            <BorderControls
              value={config.border}
              onChange={(border) => updateField('border', border)}
              showRadius={false}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="responsive">
          <AccordionTrigger>Responsive</AccordionTrigger>
          <AccordionContent>
            <ResponsiveControls
              value={config.responsive}
              onChange={(responsive) => updateField('responsive', responsive)}
            />
          </AccordionContent>
        </AccordionItem>

      </Accordion>
    </div>
  );
}
